import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { lessonApi } from '@/lib/api-client';
import type {
  PipelineStatusResponse,
  StepView,
  StepState,
} from '@/types/pipeline';
import { useIsSessionExpired } from '@/lib/session-state';
import { queryKeys } from '@/config/query-client';
import { onDemandKeys } from '@/hooks/lesson/useOnDemandContent';

/** How often to ask while only the visuals are still being drawn (they take
 *  minutes, and nothing else on the page waits for them). */
const VISUALS_POLL_MS = 8000;

/** The lesson's views that show concept visuals, read again when they land. */
export function visualQueryKeys(lessonId: string): QueryKey[] {
  return [
    queryKeys.lessons.text(lessonId),
    queryKeys.lessons.highlights(lessonId),
    queryKeys.lessons.mindmap(lessonId),
    onDemandKeys.text(lessonId),
    onDemandKeys.mindmap(lessonId),
    onDemandKeys.quiz(lessonId),
    onDemandKeys.flashcards(lessonId),
    onDemandKeys.glossary(lessonId),
    onDemandKeys.concepts(lessonId),
  ];
}

/**
 * Poll the aggregated pipeline-status endpoint while the lesson is in flight.
 *
 * The backend returns a fully-resolved `steps` map (every registry step with a
 * definitive {@link StepView}), so callers never infer state from absence -
 * `stepView(step)` is the single accessor every panel uses. One poll drives all
 * panels; component-level artifact GETs still fire on demand once a step reads
 * `completed`.
 */
export function usePipelineStatus(
  lessonId: string | undefined,
  options?: { intervalMs?: number; enabled?: boolean }
) {
  const intervalMs = options?.intervalMs ?? 3000;
  const isSessionExpired = useIsSessionExpired();
  // Gate polling while the session-expired banner is up - every poll would
  // 401 and re-emit the expiry event. Resumes on re-auth.
  const enabled = (options?.enabled ?? true) && !!lessonId && !isSessionExpired;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.lessons.pipelineStatus(lessonId ?? ''),
    queryFn: () => lessonApi.getPipelineStatus(lessonId!),
    refetchInterval: query => {
      const data = query.state.data as PipelineStatusResponse | undefined;
      if (!data) return intervalMs;
      // Keep polling while any individual step is still in flight. On-demand
      // steps (podcast/flashcards/quiz/glossary) are generated *after* the
      // lesson itself is already terminal, so they don't move
      // `overall_status` - keying off it alone would stop polling the instant
      // the lesson completes and freeze the UI on a stale "Preparing" snapshot
      // through the entire on-demand run (never observing processing→failed).
      const anyStepActive = Object.values(data.steps ?? {}).some(
        v => v.state === 'queued' || v.state === 'processing'
      );
      if (anyStepActive) return intervalMs;
      // The visuals are drawn after every step is done: keep asking, more
      // slowly, until they land.
      if (data.visuals_pending) return VISUALS_POLL_MS;
      // No step in flight - stop once the lesson lifecycle itself is terminal.
      // `steps` is seeded incrementally (overview first; content steps appear
      // after fan-out), so "all known steps terminal" would false-positive in
      // the gap between phases. `overall_status` is the lesson-level signal.
      if (
        data.overall_status === 'completed' ||
        data.overall_status === 'failed'
      ) {
        return false;
      }
      return intervalMs;
    },
    staleTime: 0,
    enabled,
  });

  // Every view that shows visuals reads the lesson again when the visuals
  // run moves on: once it has chosen them (a placeholder where each will
  // go), and once they land (the placeholders become the visuals).
  const visualsPending = !!query.data?.visuals_pending;
  const visualsStage = visualsPending
    ? query.data?.visuals_planned
      ? 'drawing'
      : 'choosing'
    : 'idle';
  const lastStage = useRef<string | null>(null);
  useEffect(() => {
    const before = lastStage.current;
    lastStage.current = visualsStage;
    const moved =
      (before === 'choosing' && visualsStage === 'drawing') ||
      (before !== null && before !== 'idle' && visualsStage === 'idle');
    if (moved && lessonId) {
      for (const queryKey of visualQueryKeys(lessonId)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    }
  }, [visualsStage, lessonId, queryClient]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    /** The full per-step map (every registry step present once loaded). */
    steps: query.data?.steps,
    /** Resolved view for a single step, or undefined until the first poll. */
    stepView: (step: string): StepView | undefined => query.data?.steps?.[step],
  };
}

export type { PipelineStatusResponse, StepView, StepState };
